import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { FiX } from 'react-icons/fi';
import useApi, { ApiMethod } from '~/hooks/useApi';
import endpoints from '~/services/endpoints';
import { MARKER_OPTIONS } from '../mocks';
import { AuditRecentVisit, VisitaDetalheResponse } from '../models';
import * as St from '../styles';
import { formatNumber } from '../utils';
import CardStatus from './CardStatus';

interface VisitCallsModalProps {
  visit: AuditRecentVisit;
  onClose: () => void;
}

const markerLabel = (marcador: string) =>
  MARKER_OPTIONS.find((option) => option.id === marcador)?.value ?? marcador;

const formatTime = (iso: string) =>
  moment(iso, 'YYYY-MM-DDTHH:mm:ss').format('DD/MM HH:mm:ss');

// Chamadas de API de uma visita (GET /visitas/{correlationId}), em ordem
// cronológica. O back devolve até 500; totalChamadas traz o total real.
const VisitCallsModal: React.FC<VisitCallsModalProps> = ({
  visit,
  onClose,
}) => {
  const [detail, setDetail] = useState<VisitaDetalheResponse>();
  const [error, setError] = useState<string>();
  const closeRef = useRef<HTMLButtonElement>(null);

  // A visita não muda enquanto o modal está aberto, então a URL é fixa.
  const api = useApi<VisitaDetalheResponse>(
    `${endpoints.consult.getAccessControlActivity}/visitas/${encodeURIComponent(
      visit.correlationId,
    )}`,
    ApiMethod.GET,
    {},
    true,
  );

  useEffect(() => {
    let active = true;
    api
      .callApi()
      .then((response) => {
        if (!active) return;
        if (response.success) {
          setDetail(response.data);
        } else {
          setError(
            response.status === 404
              ? 'Visita não encontrada.'
              : 'Não foi possível carregar as chamadas desta visita.',
          );
        }
      })
      .catch(() => {
        if (active)
          setError('Não foi possível carregar as chamadas desta visita.');
      });
    return () => {
      active = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);

  const calls = detail?.chamadas ?? [];
  const total = detail?.totalChamadas ?? visit.chamadas;

  return ReactDOM.createPortal(
    <St.ModalOverlay onMouseDown={onClose}>
      <St.ModalSheet
        role="dialog"
        aria-modal="true"
        aria-labelledby="visit-calls-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <St.ModalHeader>
          <div>
            <h3 id="visit-calls-title">Chamadas de API da visita</h3>
            <span>
              {visit.tela} · {visit.usuario} · {visit.dataHora}
            </span>
            <small>{visit.correlationId}</small>
          </div>
          <button
            ref={closeRef}
            type="button"
            aria-label="Fechar"
            onClick={onClose}
          >
            <FiX />
          </button>
        </St.ModalHeader>

        <CardStatus loading={!detail && !error} error={error} height={240}>
          <St.ModalSummary>
            {formatNumber(total)} {total === 1 ? 'chamada' : 'chamadas'}
            {calls.length < total &&
              ` (exibindo as primeiras ${formatNumber(calls.length)})`}
          </St.ModalSummary>
          <St.ScrollList maxHeight={420}>
            <St.Table minWidth={900}>
              <thead>
                <tr>
                  <th>HORÁRIO</th>
                  <th>SERVIÇO</th>
                  <th>MÉTODO</th>
                  <th>PATH / ENDPOINT</th>
                  <th>QUERY PARAMS</th>
                  <th>MARCADOR</th>
                </tr>
              </thead>
              <tbody>
                {calls.length === 0 && (
                  <tr>
                    <td colSpan={6}>
                      Nenhuma chamada registrada nesta visita.
                    </td>
                  </tr>
                )}
                {calls.map((call, index) => (
                  // A mesma chamada pode se repetir na visita; o índice desempata.
                  // eslint-disable-next-line react/no-array-index-key
                  <tr key={`${call.created}-${call.path}-${index}`}>
                    <td>{formatTime(call.created)}</td>
                    <td>{call.service}</td>
                    <td>{call.method}</td>
                    <td className="url">
                      {call.path}
                      {call.endpoint && <small>{call.endpoint}</small>}
                    </td>
                    <td className="url">{call.queryParams || '-'}</td>
                    <td>{markerLabel(call.marcador)}</td>
                  </tr>
                ))}
              </tbody>
            </St.Table>
          </St.ScrollList>
        </CardStatus>
      </St.ModalSheet>
    </St.ModalOverlay>,
    document.body,
  );
};

export default VisitCallsModal;
